import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  Select,
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../components/layout/PageLayout';
import promoService from '../../services/promoService';
import { useUser } from '../../context/UserContext';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

const RzpOfferList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [offers, setOffers] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} offers`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: null
  });

  const getOffers = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        ...filters,
        ...params
      };
      
      console.log('Fetching offers with params:', requestParams);
      const response = await promoService.getAllRzpOffers(requestParams);
      console.log('Offers API Response:', response);
      
      if (response.data && response.data.status) {
        const offersData = response.data.data || [];
        
        console.log('Offers data:', offersData);
        
        // Ensure offersData is an array
        if (Array.isArray(offersData)) {
          setOffers(offersData);
          setPagination(prev => ({
            ...prev,
            total: offersData.length
          }));
        } else {
          console.error('Offers data is not an array:', offersData);
          setOffers([]);
        }
      } else {
        message.error('Failed to fetch offers');
        setOffers([]);
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      message.error('Failed to fetch offers');
      setOffers([]);
    } finally {
      setLoading(false);
    }
  }, [filters.search, filters.status]);

  // Load offers data
  useEffect(() => {
    getOffers();
  }, [getOffers]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getOffers({
      search: value,
      status: filters.status
    });
  };

  // Handle status filter change
  const handleStatusChange = (value) => {
    setFilters(prev => ({ ...prev, status: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getOffers({
      search: filters.search,
      status: value
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
  };

  // Handle add offer
  const handleAddOffer = () => {
    navigate('/promo/promo-rzp-offer-form/new');
  };

  // Handle edit offer
  const handleEditOffer = (rzpOfferId) => {
    navigate(`/promo/promo-rzp-offer-form/${rzpOfferId}`);
  };

  // Table columns
  const columns = [
    {
      title: 'Offer ID',
      dataIndex: 'rzp_offer_id',
      key: 'rzp_offer_id',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.rzp_offer_id.localeCompare(b.rzp_offer_id),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Razorpay Offer Name',
      dataIndex: 'rzp_offer_name',
      key: 'rzp_offer_name',
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 120,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditOffer(record.rzp_offer_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage Razorpay Offers</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="Razorpay Offers List"
      desc="Manage Razorpay offers and their status"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              Offers ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddOffer}
              size="large"
            >
              Add Razorpay offer
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Select
              value={filters.status}
              onChange={handleStatusChange}
              placeholder="Status"
              style={{ width: 150 }}
              allowClear
            >
              <Option value={null}>All</Option>
              <Option value="Y">Active</Option>
              <Option value="N">Inactive</Option>
            </Select>
            
            <Search
              placeholder="Search offers..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={offers}
          rowKey="_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
          size="middle"
          locale={{ 
            emptyText: 'No offers found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default RzpOfferList;